import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { api, generateQueryKey } from "@/lib/api";

export interface TouchpointPerson {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  job_title: string | null;
  is_employee: boolean;
}

// Helper function to calculate sort order based on role and employee status
export function calculateSortOrder(
  isEmployee: boolean,
  roleInTouchpoint: string | undefined,
): number {
  if (isEmployee) {
    if (roleInTouchpoint === "sender") return 98;
    if (roleInTouchpoint === "recipient") return 99;
    return 100; // other employee roles
  } else {
    if (roleInTouchpoint === "sender") return 0;
    if (roleInTouchpoint === "recipient") return 1;
    return 2; // other non-employee roles
  }
}

export function useTouchpointPersons(accountId?: string) {
  const queryKey = generateQueryKey(
    `datahub/accounts/${accountId}/touchpoint-persons/`,
  );

  const {
    data: persons = [],
    isLoading,
    error,
    ...rest
  } = useQuery<TouchpointPerson[]>({
    queryKey,
    queryFn: () =>
      api.get<TouchpointPerson[]>(
        `datahub/accounts/${accountId}/touchpoint-persons/`,
      ),
    enabled: !!accountId,
  });

  // Create memoized lookup maps
  const { personByIdMap, personByEmailMap } = useMemo(() => {
    const byId = new Map<string, TouchpointPerson>();
    const byEmail = new Map<string, TouchpointPerson>();

    persons.forEach((person) => {
      // Store by person_id which is the key identifier
      byId.set(person.person_id, person);
      if (person.email_address) {
        // Store normalized email for case-insensitive lookup
        byEmail.set(person.email_address.toLowerCase().trim(), person);
      }
    });

    return {
      personByIdMap: byId,
      personByEmailMap: byEmail,
    };
  }, [persons]);

  // Helper to format person's name consistently
  const formatPersonName = useCallback((person: TouchpointPerson) => {
    const name = [person.first_name, person.last_name]
      .filter(Boolean)
      .join(" ");
    return name || person.email_address || person.person_id;
  }, []); // No dependencies needed for pure formatting logic

  const getPersonById = useCallback(
    (personId: string) => personByIdMap.get(personId),
    [personByIdMap],
  );

  const getPersonName = useCallback(
    (personId: string) => {
      // This function is critical - it takes the ID from activity.people[].id
      // and returns a formatted name using data from the touchpoint-persons endpoint
      if (!personId) return "Unknown";

      const person = personByIdMap.get(personId);
      if (!person) {
        return personId;
      }
      return formatPersonName(person);
    },
    [personByIdMap, formatPersonName],
  );

  const getPersonEmail = useCallback(
    (personId: string) => {
      if (!personId) return "";
      const person = personByIdMap.get(personId);
      if (!person) return "";
      return person.email_address || "";
    },
    [personByIdMap],
  );

  const getNameByEmail = useCallback(
    (emailAddress?: string | null) => {
      // Return empty string if email is null/undefined
      if (!emailAddress) return "";

      // Normalize email for comparison
      const normalizedEmail = emailAddress.toLowerCase().trim();

      // Lookup person by email
      const person = personByEmailMap.get(normalizedEmail);

      if (person) {
        return formatPersonName(person);
      }

      // If no match found, return the original email
      return emailAddress;
    },
    [personByEmailMap, formatPersonName],
  );

  // New function to check if a person is an employee
  const isEmployee = useCallback(
    (personId: string) => {
      if (!personId) return false;
      const person = personByIdMap.get(personId);
      return person?.is_employee || false;
    },
    [personByIdMap],
  );

  // New function to calculate sort order for a person
  const getPersonSortOrder = useCallback(
    (personId: string, roleInTouchpoint?: string) => {
      if (!personId) return 99;
      const person = personByIdMap.get(personId);
      if (!person) return 99; // Default high sort order for missing persons
      return calculateSortOrder(person.is_employee, roleInTouchpoint);
    },
    [personByIdMap],
  );

  // Sort people by their roles and employee status
  const sortPeopleByTpRoleAndSelectionStatus = useCallback(
    (
      people: Array<{ id: string; role_in_touchpoint?: string }>,
      selectedPeopleIds: string[] = [],
    ) => {
      if (!people || !Array.isArray(people) || people.length === 0) {
        return [];
      }

      // Assign sort orders to people based on their employee status and role
      const peopleWithSortOrder = people.map((person) => {
        if (!person || typeof person !== "object") {
          return { id: "unknown", sort_order: 99 };
        }

        const personId = person.id || "unknown";
        const role = person.role_in_touchpoint;

        return {
          ...person,
          id: personId,
          sort_order: getPersonSortOrder(personId, role),
        };
      });

      // Sort people based on selected status first, then by sort order
      return [...peopleWithSortOrder].sort((a, b) => {
        // First prioritize selected people if any are selected
        if (selectedPeopleIds.length > 0) {
          const aSelected = selectedPeopleIds.includes(a.id);
          const bSelected = selectedPeopleIds.includes(b.id);
          if (aSelected && !bSelected) return -1;
          if (!aSelected && bSelected) return 1;

          // If both are selected or both are not selected, then sort by sort_order
          if ((aSelected && bSelected) || (!aSelected && !bSelected)) {
            return a.sort_order - b.sort_order;
          }
        }

        // If no people are selected, fall back to sort_order
        return a.sort_order - b.sort_order;
      });
    },
    [getPersonSortOrder],
  );

  return {
    persons,
    getPersonById,
    getPersonName,
    getPersonEmail,
    getNameByEmail,
    isEmployee,
    getPersonSortOrder,
    sortPeopleByTpRoleAndSelectionStatus,
    isLoading,
    error,
    ...rest,
  };
}
